<template>
  <div class="container">

    <div class="card filter-section" :class="{ 'filter-collapsed': !filterVisible }">
      <div class="card-body">
        <div class="row">
          <div v-if="!filterVisible" class="filter-toggle-icon" @click="toggleFilter">
            <i class="fa fa-search"></i>
          </div>
          <div v-else>
            <div class="row">
              <div class="col-md-5">
                <label for="from-date">From</label>
                <div class="input-group">
                  <Datepicker1 class="datepicker-here form-control digits" v-model="transactionStore.filters.fromDate" :format="'dd/MM/yyyy'" :time-picker="false" data-language="en" placeholder="Select date" />
                </div>
              </div>
              <div class="col-md-5">
                <label for="to-date">To</label>
                <div class="input-group">
                  <Datepicker1 class="datepicker-here form-control digits" v-model="transactionStore.filters.toDate" :format="'dd/MM/yyyy'" :time-picker="false" data-language="en" placeholder="Select date" />
                </div>
              </div>
              <div class="col-md-5 mt-2">
                <label for="transaction-type">Transaction type</label>
                <div class="input-group">
                  <select id="transaction-type" class="form-control" v-model="transactionStore.filters.transactionType">
                    <option value="">All</option>
                    <option value="Sale">Sale</option>
                    <option value="Fee">Fee</option>
                    <option value="Refund">Refund</option>
                  </select>
                </div>
              </div>
              <div class="col-md-5 mt-2">
                <label for="reference">Settlement reference</label>
                <div class="input-group">
                  <input type="text" id="reference" class="form-control" placeholder="0123456" v-model="transactionStore.filters.reference" />
                </div>
              </div>
            </div>
            <div class="row mt-2 justify-content-end">
              <div class="col-xxl-2 col-md-4 text-end">
                <button class="btn btn-green" @click="search">Search</button>
                <button class="btn" @click="toggleFilter"><i class="fa fa-chevron-up"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="table-responsive signal-table" v-if="hasResults">
        <table class="table table-hover">
          <thead>
            <tr>
              <th class="col-date">Date/Time</th>
              <th class="col-type">Type</th>
              <th class="col-status">Transaction status</th>
              <th class="col-value">Face value</th>
              <th class="col-commission">Commission</th>
              <th class="col-pay">We pay you</th>
              <th class="col-payment">Payment status</th>
              <th class="col-info">Info</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(transactions, date) in paginatedData" :key="date">
              <tr>
                <td colspan="8"><strong>{{ format(new Date(date)) }}</strong></td> 
              </tr>
              <tr v-for="(item, index) in transactions" :key="index">
                <td>{{ formatTime(item.datetime) }}</td>
                <td>
                  <div v-if="formatToLowerCase(item.type) === 'eftpos'">
                    <img class="eftpos-icon" src="@/assets/images/admin/transactions/eftpos.png" alt="Eftpos" />
                  </div>
                  <div v-if="formatToLowerCase(item.type) === 'visa'">
                    <img class="eftpos-icon" src="@/assets/images/admin/transactions/visa.png" alt="Visa" />
                  </div>
                  <div v-if="formatToLowerCase(item.type) === 'mastercard'">
                    <img class="eftpos-icon" src="@/assets/images/admin/transactions/mastercard.png" alt="Mastercard" />
                  </div>
                  <div v-if="formatToLowerCase(item.type) === 'american express'">
                    <img class="eftpos-icon" src="@/assets/images/admin/transactions/amex.png" alt="Amex" />
                  </div>
                  <div v-if="item.type === 'Fee' || item.type === 'Chargeback' || item.type === 'Refund'">{{ item.type }}</div>
                </td>
                <td :class="{ 'text-green': item.transaction_status === 'Approved', 'text-danger': item.transaction_status === 'Declined' }">{{ item.transaction_status }}</td>
                <td v-if="item.face_value != null">{{ formatCurrency(item.face_value) }}</td>
                <td v-else>{{ (item.face_value) }}</td>
                <td v-if="item.commission != null">{{ formatCurrency(item.commission) }}</td>
                <td v-else>{{ item.commission }}</td>
                <td :class="{ '': parseFloat(item.we_pay_you) > 0, 'text-danger': parseFloat(item.we_pay_you) < 0 }">{{ formatCurrency(item.we_pay_you) }}</td>
                <td :class="{ 'text-green': item.payment_status === 'Settled', 'text-secondary': item.payment_status === 'Pending' }">{{ item.payment_status }}</td>
                <td @click="showModal(item, index)">
                  <i class="fa fa-arrow-right info-icon"></i>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
        <div class="pagination-wrapper">
          <div class="pagination-container">
            <button @click="changePage(1)" :disabled="transactionStore.currentPage === 1"><img  src="@/assets/images/admin/arrow/prev-end.png" alt="Prev-end" style="width: 25px; height: 25px;" /></button>
            <button @click="changePage(transactionStore.currentPage - 1)" :disabled="transactionStore.currentPage === 1"><img  src="@/assets/images/admin/arrow/prev.png" alt="Prev" style="width: 25px; height: 25px;" /></button>
            <span>Page <input type="number" v-model.number="transactionStore.currentPage" @change="changePage(transactionStore.currentPage)" /> of {{ totalPages }}</span>
            <button @click="changePage(transactionStore.currentPage + 1)" :disabled="transactionStore.currentPage === totalPages"><img  src="@/assets/images/admin/arrow/next.png" alt="Next" style="width: 25px; height: 25px;" /></button>
            <button @click="changePage(totalPages)" :disabled="transactionStore.currentPage === totalPages"><img  src="@/assets/images/admin/arrow/next-end.png" alt="Next-end" style="width: 25px; height: 25px;" /></button>
          </div>
          <div class="pagination-summary">
            Displaying transactions {{ startTransaction }} – {{ endTransaction }} of {{ totalEntries }}
          </div>
        </div>
      </div>
      <div v-else class="no-results">No results found.</div>
    </div>
       <div class="modal fade" id="detailsModal" tabindex="-1" aria-labelledby="detailsModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-dialog-slideout modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <button :disabled="!hasPrevItem" class="btn" @click="showPrev"><i class="fa fa-arrow-left"></i></button>
            <button :disabled="!hasNextItem" class="btn" @click="showNext"><i class="fa fa-arrow-right"></i></button>
            <button class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- 判斷 transaction type，顯示不同的內容 -->
            <div v-if="['Fee', 'Chargeback', 'Refund'].includes(selectedItem.type)">
              <div>
                <p class="detail-line">
                  <span class="label-1">Date:</span>
                  <span class="f-w-500">{{ formattedDate }}</span>
                </p>
                <p class="detail-line">
                  <span>Time:</span>
                  <span class="f-w-500">{{ formattedTime }}</span>
                </p>
                <hr>
                <p class="detail-line">
                  <span>Transaction type:</span>
                  <strong :class="selectedItem.type == 'Refund' ? 'text-green' : 'text-danger'">
                    {{ selectedItem.type }}
                  </strong> 
                </p>
                <p class="detail-line">
                  <span>Description:</span>
                  <span class="f-w-500">{{ selectedItem.description }}</span>
                </p>
                <hr>
                <p class="detail-line">
                  <strong>Amount:</strong>
                  <strong>{{ formatCurrency(selectedItem.we_pay_you) }}</strong>
                </p>
                <hr>
                <p class="detail-line">
                  <span>Settlement Reference:</span>
                  <span class="f-w-500">{{ selectedItem.settlement_ref }}</span>
                </p>
              </div>
            </div>
            <div v-else>
              <div>
                <p class="detail-line">
                  <span>Date:</span>
                  <span class="f-w-500">{{ formattedDate }}</span>
                </p>
                <p class="detail-line">
                  <span>Time:</span>
                  <span class="f-w-500">{{ formattedTime }}</span>
                </p>
                <hr>
                <p class="detail-line">
                  <span>Transaction reference:</span>
                  <span class="f-w-500">{{ selectedItem.transaction_ref }}</span>
                </p>
                <p class="detail-line">
                  <span>Transaction type:</span>
                  <span class="f-w-500">{{ selectedItem.transaction_type }}</span >
                </p>
                <p class="detail-line">
                  <span>Terminal:</span>
                  <span class="f-w-500">{{ selectedItem.terminal }}</span>
                </p>
                <p class="detail-line">
                  <span>Card type:</span>
                  <div v-if="formatToLowerCase(selectedItem.card_type) === 'eftpos'">
                    <img class="eftpos-icon" src="@/assets/images/admin/transactions/eftpos.png" alt="Eftpos" />
                  </div>
                  <div v-if="formatToLowerCase(selectedItem.card_type) === 'visa'">
                    <img class="eftpos-icon" src="@/assets/images/admin/transactions/visa.png" alt="Visa" />
                  </div>
                  <div v-if="formatToLowerCase(selectedItem.card_type) === 'mastercard'">
                    <img class="eftpos-icon" src="@/assets/images/admin/transactions/mastercard.png" alt="Mastercard" />
                  </div>
                  <div v-if="formatToLowerCase(selectedItem.card_type) === 'american express'">
                    <img class="eftpos-icon" src="@/assets/images/admin/transactions/amex.png" alt="Amex" />
                  </div>
                </p>
                <p class="detail-line">
                  <span>Card last 4 digits:</span>
                  <span class="f-w-500">{{ selectedItem.card_last_4_digits }}</span>
                </p>
                <p class="detail-line">
                  <span>Status:</span>
                  <strong :class="{ 'text-green': selectedItem.transaction_status === 'Approved', 'text-danger': selectedItem.transaction_status !== 'Approved' }">
                    {{ selectedItem.transaction_status }}
                  </strong>
                </p>
                <hr>
                <p class="detail-line">
                  <span>Face value:</span>
                  <span class="f-w-500">{{ formatCurrency(selectedItem.face_value) }}</span>
                </p>
                <p class="detail-line">
                  <span>Commission:</span>
                  <span class="f-w-500">{{ formatCurrency(selectedItem.commission) }}</span>
                </p>
                <hr>
                <p class="detail-line">
                  <strong>We pay you:</strong>
                  <strong class="f-w-700 f-24">{{ formatCurrency(selectedItem.we_pay_you) }}</strong>
                </p>
                <br>
                <hr>
                <p class="detail-line">
                  <span>Payment status:</span>
                  <strong :class="{ 'text-green': selectedItem.payment_status === 'Settled', 'text-secondary': selectedItem.payment_status !== 'Settled' }">
                    {{ selectedItem.payment_status }}
                  </strong>
                </p>
                <p class="detail-line">
                  <span>Settlement Reference:</span>
                  <span class="f-w-500">{{ selectedItem.settlement_ref }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useTransactionStore } from '@/store/driver/transaction';
import { Modal } from 'bootstrap';
import Datepicker1 from '@vuepic/vue-datepicker';
import { onBeforeRouteLeave } from 'vue-router';  
import '@vuepic/vue-datepicker/dist/main.css';

const transactionStore = useTransactionStore();

const filterVisible = ref(false);
const selectedItem = ref({});
const currentIndex = ref(0);
const formattedDate = ref('');
const formattedTime = ref('');
let bsModal;

// 修改日期格式為 dd-mm-yy
const format = (date) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear().toString().slice(-2); // 取後兩位數
  return `${day}-${month}-${year}`;
};

// 修改時間格式為 hh:mm
const formatTime = (datetime) => {
  const date = new Date(datetime);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// 格式化貨幣函數
const formatCurrency = (value) => {
  const absValue = Math.abs(value);
  return value < 0 ? `-$${absValue}` : `$${absValue}`;
};

const formatToLowerCase = (str) => {
  return str ? str.toLowerCase() : ''; // 如果 str 存在則轉為小寫，否則返回空字串
};


const toggleFilter = () => {
  filterVisible.value = !filterVisible.value;
};

const search = async () => {
  const filters = {
    fromDate: transactionStore.filters.fromDate,
    toDate: transactionStore.filters.toDate,
    transactionType: transactionStore.filters.transactionType !== 'all' ? transactionStore.filters.transactionType : '',
    reference: transactionStore.filters.reference
  };

  transactionStore.currentPage = 1;
  await transactionStore.fetchTransactions(1, transactionStore.itemsPerPage, filters);
};

const changePage = async (page) => {
  transactionStore.changePage(page);
};



// 重置篩選條件
const resetFilters = () => {
  transactionStore.filters.fromDate = '';
  transactionStore.filters.toDate = '';
  transactionStore.filters.transactionType = '';
  transactionStore.filters.reference = '';
};

onMounted(async () => {
  // 等待頁面渲染完成，然後嘗試獲取 modal element
  changePage(1);
  await transactionStore.fetchTransactions();

  // 初始化模態視窗，只在 element 存在時初始化
  const modalElement = document.getElementById('detailsModal');
  if (modalElement) {
    bsModal = new Modal(modalElement, {
      backdrop: true,
      keyboard: true
    });
  }
});

onBeforeRouteLeave(() => {
  resetFilters();
  transactionStore.shouldResetFilters = false;
});

const flatTransactions = computed(() => {
  return Object.values(transactionStore.paginatedData).flat();
});

const showModal = (item, index) => {
  if (bsModal) {
    selectedItem.value = item;
    currentIndex.value = index;

    const [date, time] = item.datetime.split(' ');
    formattedDate.value = format(new Date(date)); // 使用格式化日期
    formattedTime.value = formatTime(item.datetime); // 使用格式化時間

    bsModal.show();
  } else {
    console.error('Modal is not initialized.');
  }
};

// 確認是否有結果
const hasResults = computed(() => transactionStore.totalEntries > 0);
const paginatedData = computed(() => transactionStore.paginatedData);
const totalPages = computed(() => transactionStore.totalPages);
const totalEntries = computed(() => transactionStore.totalEntries);
const startTransaction = computed(() => (transactionStore.currentPage - 1) * transactionStore.itemsPerPage + 1);
const endTransaction = computed(() => Math.min(transactionStore.currentPage * transactionStore.itemsPerPage, totalEntries.value));

// 確認是否有上一筆或下一筆資料
const hasPrevItem = computed(() => currentIndex.value > 0);
const hasNextItem = computed(() => currentIndex.value < flatTransactions.value.length - 1);

// 顯示上一筆資料
const showPrev = () => {
  if (hasPrevItem.value) {
    currentIndex.value -= 1;
    selectedItem.value = flatTransactions.value[currentIndex.value];
    
    const [date, time] = selectedItem.value.datetime.split(' ');
    formattedDate.value = format(new Date(date));
    formattedTime.value = formatTime(selectedItem.value.datetime);
  }
};

// 顯示下一筆資料
const showNext = () => {
  if (hasNextItem.value) {
    currentIndex.value += 1;
    selectedItem.value = flatTransactions.value[currentIndex.value];
    
    const [date, time] = selectedItem.value.datetime.split(' ');
    formattedDate.value = format(new Date(date));
    formattedTime.value = formatTime(selectedItem.value.datetime);
  }
};
</script>



<style scoped>
.container {
  position: relative;
  min-height: 100vh;
}

.filter-section {
  margin-bottom: 20px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
}

.filter-collapsed .card-body {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.filter-toggle-icon {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-size: 24px;
  color: #666;
  cursor: pointer;
}

.table-responsive {
  padding: 20px;
}

.signal-table .table-hover tbody tr:hover {
  background-color: #f5f5f5;
}

.text-black {
  color: black;
}

span,strong {
  font-size: 18px;
}

thead th {
  position: sticky;
  top: 0;
  background: #fff;
}

.info-icon {
  font-size: 20px;
  padding: 10px;
  border-radius: 20%;
  cursor: pointer;
  color: #8476f4;
}

.signal-table.table-responsive .table tbody tr td:nth-child(n+2) {
  min-width: 100px !important;
}

.info-icon:hover {
  background-color: #8476f4;
  color: white;
}

.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
}

.pagination-container {
  display: flex;
  align-items: center;
}

.pagination-container button {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: #8476f4;
}

.pagination-container span {
  margin: 0 10px;
}

.pagination-container input {
  width: 50px;
  text-align: center;
}

.modal-dialog-slideout {
  max-width: 485px;
  width: 60%;
  margin: 0;
  height: 100%;
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  transition: transform 0.3s ease-out;
}

.modal-content {
  height: 100%;
  border-radius: 0;
  padding-right: 20px;
  padding-left: 20px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
}

.modal-body {
  overflow-y: auto;
  flex-grow: 1;
}

.detail-line {
  display: flex;
  justify-content: space-between;
  padding-bottom: 5px;
}

.detail-custom {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 16px;
}

.modal-body p {
  margin: 0 0 10px;
}

.btn-green {
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 10px 32px;
  gap: 10px;
  margin-left: 20px;
  width: 150px;
  height: 46px;
  background: #00c962;
  border-radius: 30px;
  flex: none;
  order: 1;
  flex-grow: 0;
}

.btn-loadmore {
  font-size: 16px;
  color: #8476f4;
}

.btn-green:hover {
  background-color: #00c962;
}

.text-green {
  color: #00c962;
}

.load-img {
  margin-bottom: 5px;
  margin-left: 20px;
  width: 20px;
  height: 20px;
}

.modal-body .text-danger {
  color: red;
}

.detail-box {
  border-bottom: 2px solid #d9dde3;
  margin-top: 50px;
}

.detail-total {
  margin-top: 50px;
}

.line-custom {
  border-bottom: 2px ridge #f9f9fa;
}

.eftpos-icon {
  width: 50px;
  height: auto;
}

.detail-download {
  width: auto;
  border: 2px inset #d9dde3;
  padding: 10px;
}

.detail-download i {
  font-size: 20px;
  margin-left: 100px;
}

.view-trans {
  padding-top: 30px;
  padding-bottom: 30px;
}

.no-results {
  border-radius: 20px;
  padding: 50px;
  font-size: 20px;
  text-align: center;
  color: black;
  background-color: #ccd3da;
}

@media (max-width: 630px) {
  .pagination-wrapper {
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  .pagination-summary {
    margin-top: 10px;
    justify-content: center;
  }

  .pagination-container {
    justify-content: center;
    width: 100%;
    margin-bottom: 10px;
  }
}

@media (max-width: 1366px) {
  .modal-dialog-slideout {
    max-width: 100vw;
    width: 100%;
    margin: 0;
    height: 100vh;
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    transition: none;
    border-radius: 0;
  }

  .modal-content {
    height: 100%;
    border-radius: 0;
  }
}
</style>
